import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MagnifyingGlass, BookOpen, Warning, X, ArrowSquareOut, Lightning, Database } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  KnowledgeBasePanel — public Aᴳ ingredient database browser for clients.
 *
 *  Surfaces a regulator-facing view of the 4,785-ingredient Aᴳ Masterlist:
 *  INCI · CAS · function class · regulatory flags (EU II/III, IFRA, Prop 65,
 *  Prohibited group) · qualitative tox flags (genotoxicity, mutagenicity,
 *  carcinogenic, reproductive, phototoxic, sensitisation category).
 *
 *  Internal NOAEL / data-gap text / assessor rationale are deliberately
 *  withheld here — those stay in the Aᴳ assessor console.
 * ======================================================================= */

const FLAG_CHIPS = [
  { id: '',              label: 'All', tone: 'bg-gray-100 text-[#111827] border-gray-300' },
  { id: 'prohibited',    label: 'Prohibited (Aᴳ)',  tone: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]' },
  { id: 'eu_annex_ii',   label: 'EU Annex II',      tone: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]' },
  { id: 'eu_annex_iii',  label: 'EU Annex III',     tone: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]' },
  { id: 'ifra',          label: 'IFRA Restricted',  tone: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]' },
  { id: 'prop65',        label: 'Prop 65',          tone: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]' },
];

function FlagBadge({ label, tone }) {
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded ${tone}`}>{label}</span>;
}

function IngredientRow({ ing, onSelect }) {
  const flags = [];
  if (ing.prohibited)     flags.push(<FlagBadge key="ph"   label={ing.prohibited_group || 'Prohibited'} tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />);
  if (ing.eu_annex_ii)    flags.push(<FlagBadge key="e2"   label="EU II" tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />);
  if (ing.eu_annex_iii)   flags.push(<FlagBadge key="e3"   label="EU III" tone="bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]" />);
  if (ing.ifra_restricted) flags.push(<FlagBadge key="ifra" label="IFRA" tone="bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]" />);
  if (ing.prop_65)        flags.push(<FlagBadge key="p65"  label="Prop 65" tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />);
  const tox = [];
  if (ing.genotoxicity)        tox.push('Genotox');
  if (ing.mutagenicity)        tox.push('Mutagen');
  if (ing.carcinogenic)        tox.push('Carc');
  if (ing.reproductive_toxic)  tox.push('Reprotox');
  if (ing.phototoxic)          tox.push('Phototox');

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => onSelect(ing)} data-testid={`kb-row-${ing.inci_name}`}>
      <td className="py-2 px-3 font-semibold text-[#111827] text-[13px]">{ing.inci_name}</td>
      <td className="py-2 px-3 font-mono text-[11px] text-[#4B5563]">{ing.cas_number || '—'}</td>
      <td className="py-2 px-3 text-[12px] text-[#4B5563]">{ing.function_class || '—'}</td>
      <td className="py-2 px-3 text-[12px] text-[#4B5563]">{ing.skin_sensitization || '—'}</td>
      <td className="py-2 px-3">
        <div className="flex flex-wrap gap-1">{flags.length ? flags : <span className="text-[10px] text-[#9CA3AF]">—</span>}</div>
      </td>
      <td className="py-2 px-3 text-[11px] text-[#4B5563]">{tox.length ? tox.join(' · ') : '—'}</td>
    </tr>
  );
}

function IngredientDetail({ ing, onClose }) {
  if (!ing) return null;
  const allFlags = [
    ['Prohibited (Aᴳ)', ing.prohibited],
    ['EU Annex II — Banned in cosmetics', ing.eu_annex_ii],
    ['EU Annex III — Restricted with conditions', ing.eu_annex_iii],
    ['IFRA Restricted', ing.ifra_restricted],
    ['California Prop 65', ing.prop_65],
    ['Genotoxicity flag', ing.genotoxicity],
    ['Mutagenicity flag', ing.mutagenicity],
    ['Carcinogenicity flag', ing.carcinogenic],
    ['Reproductive toxicity', ing.reproductive_toxic],
    ['Phototoxicity', ing.phototoxic],
  ];
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose} data-testid="kb-detail-modal">
      <div className="bg-white rounded-md shadow-xl max-w-2xl w-full mt-12 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-[#111827]">{ing.inci_name}</h3>
            <p className="text-xs text-[#4B5563] font-mono">CAS {ing.cas_number || '—'} · EINECS {ing.einecs || '—'}</p>
          </div>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#111827]" data-testid="kb-detail-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4 text-[12px]">
          <div className="bg-[#F9FAFB] p-2 rounded">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Function class</p>
            <p className="font-semibold text-[#111827]">{ing.function_class || '—'}</p>
          </div>
          <div className="bg-[#F9FAFB] p-2 rounded">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Molecular weight</p>
            <p className="font-semibold text-[#111827] tabular-nums">{ing.molecular_weight ? `${ing.molecular_weight} g/mol` : '—'}</p>
          </div>
          <div className="bg-[#F9FAFB] p-2 rounded">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Sensitisation</p>
            <p className="font-semibold text-[#111827]">{ing.skin_sensitization || '—'}</p>
          </div>
          <div className="bg-[#F9FAFB] p-2 rounded">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Source</p>
            <p className="font-semibold text-[#111827] text-[11px]">{ing.source || 'Aᴳ Masterlist'}</p>
          </div>
        </div>
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-2">Regulatory &amp; hazard flags</p>
          <div className="space-y-1.5">
            {allFlags.map(([label, val]) => (
              <div key={label} className="flex items-center justify-between text-[12px] border-b border-gray-100 pb-1">
                <span className="text-[#4B5563]">{label}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${val ? 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]' : 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]'}`}>
                  {val ? 'YES' : 'NO'}
                </span>
              </div>
            ))}
          </div>
        </div>
        {ing.prohibited && ing.prohibited_rationale && (
          <div className="mb-4 bg-[#FDE8E8] border border-[#F8B4B4] rounded p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B1C1C] mb-1">Prohibition rationale</p>
            <p className="text-[12px] text-[#5C0F0F] leading-relaxed">{ing.prohibited_rationale}</p>
          </div>
        )}
        <div className="bg-[#F9FAFB] border border-gray-200 rounded p-3 text-[11px] text-[#4B5563]">
          <Warning size={12} weight="fill" className="inline mr-1 text-[#FF4E6A]" />
          This knowledge-base view is a regulator-facing summary. NOAEL values, data-gap notes, Klimisch scoring, and assessor rationale are reserved for Aᴳ-signed formal assessments. Submit an assessment from the Dashboard to receive a full report.
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  QuickLookup — single-ingredient lookup (internal-first, PubChem fallback)
 *  Returns at most ONE result, never a list.
 * ==========================================================*/

function QuickLookup() {
  const [q, setQ] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    const v = q.trim();
    if (v.length < 2) { setErr('Enter at least 2 characters'); return; }
    setLoading(true); setErr(''); setResult(null);
    try {
      const r = await axios.get(`${API_URL}/api/ingredients/lookup`,
        { params: { q: v }, withCredentials: true });
      setResult(r.data);
    } catch (ex) {
      setErr(ex?.response?.status === 404
        ? `No internal or public record found for "${v}".`
        : (ex?.response?.data?.detail || 'Lookup failed'));
    } finally { setLoading(false); }
  };

  const reset = () => { setQ(''); setResult(null); setErr(''); };

  const isInternal = result?.source === 'internal';
  const ing = result?.ingredient;

  return (
    <div className="card-flat p-5 bg-white mb-5" data-testid="quick-lookup-panel">
      <div className="flex items-center gap-2 mb-1">
        <Lightning size={20} weight="duotone" className="text-[#FF4E6A]" />
        <h2 className="text-base font-bold text-[#111827]">Quick ingredient lookup</h2>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-auto">Internal first · PubChem fallback</span>
      </div>
      <p className="text-[12px] text-[#4B5563] mb-3">
        Enter <strong>one</strong> INCI or CAS. We resolve against the Aᴳ Masterlist first; if no internal record exists, we fall back to public chemistry via PubChem. <strong>Single record returned</strong> — this is not a browse list.
      </p>

      <form onSubmit={lookup} className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Glycerin · 56-81-5 · caffeine"
            className="h-10 text-sm pl-9"
            data-testid="quick-lookup-input"
          />
        </div>
        <Button type="submit" disabled={loading} className="h-10 px-4 rounded bg-[#FF4E6A] hover:bg-[#E04057] text-white text-sm font-semibold" data-testid="quick-lookup-submit">
          {loading ? 'Searching…' : 'Look up'}
        </Button>
        {(result || err) && (
          <Button type="button" variant="outline" onClick={reset} className="h-10 px-3 rounded text-sm" data-testid="quick-lookup-reset">
            Clear
          </Button>
        )}
      </form>

      {err && (
        <div className="p-3 rounded bg-[#FDE8E8] border border-[#F8B4B4] text-[12px] text-[#9B1C1C]" data-testid="quick-lookup-error">
          {err}
        </div>
      )}

      {ing && (
        <div className={`rounded border-2 p-4 ${isInternal ? 'border-[#0B1426] bg-white' : 'border-[#9F7AEA] bg-[#FAFAFF]'}`}
             data-testid={`quick-lookup-result-${result.source}`}>
          {/* Source banner */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {isInternal ? (
                <>
                  <Database size={14} weight="duotone" className="text-[#0B1426]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1426]">Aᴳ Masterlist · {result.match_kind?.replace('_', ' ')}</span>
                </>
              ) : (
                <>
                  <ArrowSquareOut size={14} weight="duotone" className="text-[#5B21B6]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5B21B6]">PubChem fallback · external chemistry</span>
                </>
              )}
            </div>
            {ing.pubchem_cid && (
              <a href={ing.pubchem_url} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] font-semibold text-[#5B21B6] hover:underline flex items-center gap-1"
                 data-testid="quick-lookup-pubchem-link">
                CID {ing.pubchem_cid} <ArrowSquareOut size={11} weight="bold" />
              </a>
            )}
          </div>

          <h3 className="text-lg font-bold text-[#111827]">{ing.inci_name || ing.iupac_name || '—'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#4B5563]">CAS</p>
              <p className="text-[13px] font-mono text-[#111827]">{ing.cas_number || '—'}</p>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#4B5563]">{isInternal ? 'Function class' : 'Molecular formula'}</p>
              <p className="text-[13px] text-[#111827]">{ing.function_class || ing.molecular_formula || '—'}</p>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#4B5563]">{isInternal ? 'Sensitisation' : 'Mol. weight'}</p>
              <p className="text-[13px] text-[#111827]">{ing.skin_sensitization || (ing.molecular_weight ? `${ing.molecular_weight} g/mol` : '—')}</p>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#4B5563]">{isInternal ? 'Prohibited group' : 'InChIKey'}</p>
              <p className="text-[12px] font-mono text-[#111827] truncate" title={ing.prohibited_group || ing.inchikey}>
                {ing.prohibited_group || ing.inchikey || '—'}
              </p>
            </div>
          </div>

          {/* Internal: flag chips */}
          {isInternal && (
            <div className="mt-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">Regulatory & tox flags</p>
              <div className="flex flex-wrap gap-1.5">
                {ing.eu_annex_ii        && <FlagBadge label="EU Annex II"    tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />}
                {ing.eu_annex_iii       && <FlagBadge label="EU Annex III"   tone="bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]" />}
                {ing.ifra_restricted    && <FlagBadge label="IFRA restricted" tone="bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]" />}
                {ing.prop_65            && <FlagBadge label="Prop 65"         tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />}
                {ing.prohibited         && <FlagBadge label={ing.prohibited_group || 'Prohibited'} tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />}
                {ing.genotoxicity       && <FlagBadge label="Genotoxic"      tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />}
                {ing.mutagenicity       && <FlagBadge label="Mutagenic"      tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />}
                {ing.carcinogenic       && <FlagBadge label="Carcinogen"     tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />}
                {ing.reproductive_toxic && <FlagBadge label="Reprotox"       tone="bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]" />}
                {ing.phototoxic         && <FlagBadge label="Phototoxic"     tone="bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]" />}
                {!ing.eu_annex_ii && !ing.eu_annex_iii && !ing.ifra_restricted && !ing.prop_65 && !ing.prohibited
                 && !ing.genotoxicity && !ing.mutagenicity && !ing.carcinogenic && !ing.reproductive_toxic && !ing.phototoxic && (
                  <span className="text-[11px] text-[#4B5563] italic">No restrictive flags recorded.</span>
                )}
              </div>
              {ing.conclusions && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Aᴳ regulatory summary</p>
                  <p className="text-[12px] text-[#374151]">{ing.conclusions}</p>
                </div>
              )}
            </div>
          )}

          {/* External: SMILES + disclaimer */}
          {!isInternal && (
            <div className="mt-4">
              {ing.canonical_smiles && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">SMILES</p>
                  <p className="text-[11px] font-mono text-[#111827] bg-[#F3F4F6] p-2 rounded break-all">{ing.canonical_smiles}</p>
                </div>
              )}
              {ing.regulatory_disclaimer && (
                <div className="p-3 rounded bg-[#FEF3C7] border-l-4 border-[#F3CC64] flex items-start gap-2">
                  <Warning size={14} weight="duotone" className="text-[#723B13] mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-[#723B13]">{ing.regulatory_disclaimer}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function KnowledgeBasePanel() {
  const [query, setQuery] = useState('');
  const [functionClass, setFunctionClass] = useState('');
  const [flag, setFlag] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ function_classes: [], counts: {} });
  const [selected, setSelected] = useState(null);
  const limit = 50;

  // Load facets once
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/ingredients/public/facets`, { withCredentials: true });
        setFacets(r.data);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  // Debounced search
  const search = useCallback(async (q, fc, fl, sk) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (fc) params.set('function_class', fc);
      if (fl) params.set('flag', fl);
      params.set('skip', sk);
      params.set('limit', limit);
      const r = await axios.get(`${API_URL}/api/ingredients/public/search?${params.toString()}`, { withCredentials: true });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => search(query, functionClass, flag, skip), 300);
    return () => clearTimeout(handle);
  }, [query, functionClass, flag, skip, search]);

  // Reset pagination when filters change
  useEffect(() => { setSkip(0); }, [query, functionClass, flag]);

  return (
    <div data-testid="knowledge-base-wrap">
      <QuickLookup />
      <div className="card-flat p-5 bg-white" data-testid="knowledge-base-panel">
        <div className="flex items-center gap-2 mb-1">
          <BookOpen size={20} className="text-[#FF4E6A]" weight="duotone" />
          <h2 className="text-base font-bold text-[#111827]">Aᴳ Ingredient Knowledge Base</h2>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-auto tabular-nums">
            {facets.counts?.total || total} ingredients
          </span>
        </div>
        <p className="text-[12px] text-[#4B5563] mb-4">
          Searchable regulatory landscape — INCI, CAS, function class, EU bans, IFRA, Prop 65, and qualitative tox flags. NOAEL and assessor rationale are reserved for formal Aᴳ assessments.
        </p>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search INCI name or CAS number…"
            className="h-9 text-sm pl-9"
            data-testid="kb-search"
          />
        </div>
        <select
          value={functionClass}
          onChange={(e) => setFunctionClass(e.target.value)}
          className="h-9 text-sm border border-gray-300 rounded px-2 bg-white min-w-[160px]"
          data-testid="kb-function-class"
        >
          <option value="">All function classes ({facets.function_classes?.length || 0})</option>
          {(facets.function_classes || []).map(fc => <option key={fc} value={fc}>{fc}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {FLAG_CHIPS.map(c => {
          const count = c.id ? facets.counts?.[c.id === 'prop65' ? 'prop65' : c.id === 'ifra' ? 'ifra' : c.id] : facets.counts?.total;
          const isActive = flag === c.id;
          return (
            <button
              key={c.id || 'all'}
              onClick={() => setFlag(c.id)}
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border transition-colors ${isActive ? c.tone : 'bg-white text-[#4B5563] border-gray-200 hover:border-gray-400'}`}
              data-testid={`kb-flag-${c.id || 'all'}`}
            >
              {c.label} {count != null && <span className="opacity-70 ml-0.5 tabular-nums">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Results */}
      <div className="border border-gray-200 rounded overflow-hidden">
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">
                <th className="text-left py-2 px-3">INCI</th>
                <th className="text-left py-2 px-3">CAS</th>
                <th className="text-left py-2 px-3">Function</th>
                <th className="text-left py-2 px-3">Sensitisation</th>
                <th className="text-left py-2 px-3">Regulatory</th>
                <th className="text-left py-2 px-3">Tox flags</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-[#4B5563] text-sm">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-[#4B5563] text-sm">No matches</td></tr>
              ) : (
                items.map(ing => <IngredientRow key={`${ing.inci_name}-${ing.cas_number}`} ing={ing} onSelect={setSelected} />)
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-2 bg-gray-50 border-t border-gray-200 text-[11px] text-[#4B5563]">
          <span className="tabular-nums" data-testid="kb-count">{total === 0 ? '0' : `${skip + 1}–${Math.min(skip + limit, total)} of ${total.toLocaleString()}`}</span>
          <div className="flex gap-1">
            <Button variant="outline" disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - limit))} className="h-7 text-xs rounded" data-testid="kb-prev">← Prev</Button>
            <Button variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(s => s + limit)} className="h-7 text-xs rounded" data-testid="kb-next">Next →</Button>
          </div>
        </div>
      </div>

      <IngredientDetail ing={selected} onClose={() => setSelected(null)} />
      </div>
    </div>
  );
}
